/* Signed SMJ storefront API adapter.
 * Every API-rendered surface fails back to the existing Liquid storefront.
 */
(function () {
  'use strict';

  var apiConfig = window.smjB2BApiConfig || {};
  var state = {
    catalog: null,
    catalogPromise: null,
    variants: {},
    savedCartWrites: false
  };

  function apiEnabled() {
    return apiConfig.enabled === true && apiConfig.loggedIn === true;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function escapeAttribute(value) {
    return escapeHtml(value).replace(/`/g, '&#096;');
  }

  function routeRoot() {
    return window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root
      : '/';
  }

  function endpoint(path) {
    var base = String(apiConfig.baseUrl || '/apps/smj').replace(/\/+$/, '');
    return base + '/' + String(path || '').replace(/^\/+/, '');
  }

  function requestUrl(path) {
    var url = endpoint(path);
    var params = [];
    if (apiConfig.customerId) {
      params.push('customer_id=' + encodeURIComponent(apiConfig.customerId));
    }
    if (apiConfig.shop) {
      params.push('shop=' + encodeURIComponent(apiConfig.shop));
    }
    if (!params.length) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + params.join('&');
  }

  function request(path, options) {
    var requestOptions = options || {};
    var controller = window.AbortController ? new AbortController() : null;
    var timeout = Math.max(parseInt(apiConfig.timeoutMs, 10) || 4500, 1000);
    var timer = controller
      ? window.setTimeout(function () { controller.abort(); }, timeout)
      : null;
    var headers = { Accept: 'application/json' };
    if (requestOptions.body) headers['Content-Type'] = 'application/json';

    return fetch(requestUrl(path), {
      method: requestOptions.method || 'GET',
      credentials: /^https?:\/\//i.test(String(apiConfig.baseUrl || ''))
        ? 'omit'
        : 'same-origin',
      headers: headers,
      body: requestOptions.body
        ? JSON.stringify(requestOptions.body)
        : undefined,
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      if (!response.ok) throw new Error('api_' + response.status);
      return response.json();
    }).finally(function () {
      if (timer) window.clearTimeout(timer);
    });
  }

  function formatMoney(cents) {
    var amount = (Number(cents || 0) / 100).toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var format = (window.smjB2B && window.smjB2B.moneyFormat) || '${{amount}}';
    return format
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(
        /\{\{\s*amount_no_decimals\s*\}\}/,
        Math.round(Number(cents || 0) / 100).toLocaleString()
      );
  }

  function priceCents(variant) {
    return Math.round(parseFloat((variant || {}).price || 0) * 100);
  }

  function formatDate(value) {
    if (!value) return '';
    var date = new Date(value);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }

  function regularVariants(product) {
    return (product.variants || []).filter(function (variant) {
      return !variant.top_load_special && !variant.mixed_1l_20ft_helper;
    });
  }

  function additionalOrderMode(catalogPayload, key) {
    return ((catalogPayload.profile || {}).additional_order_modes || []).filter(function (mode) {
      return mode.key === key;
    })[0] || null;
  }

  function mixed20ftMode(catalogPayload) {
    return additionalOrderMode(catalogPayload, 'mixed_1l_20ft');
  }

  function mixed20ftProperties(variant) {
    return {
      _smj_case_order: 'true',
      _smj_order_unit: 'cases',
      _smj_order_profile: 'mixed_1l_20ft',
      _smj_container_capacity: String(mixed20ftMode(state.catalog).container_capacity),
      _smj_container_unit: 'cases',
      _smj_container_size: '20ft',
      _smj_original_sku: String(variant.sku || '')
    };
  }

  function profileForVariant(catalog, variant) {
    var profile = catalog.profile || {};
    if (profile.key === 'case_pallet_22') return 'case_pallet_22';
    if (profile.key === 'satex_case_2500' && variant.satex_eligible) {
      return 'satex_case_2500';
    }
    return profile.key === 'pallet_22' ? 'pallet_22' : 'standard';
  }

  function orderUnit(catalog, variant) {
    var profile = profileForVariant(catalog, variant);
    return profile === 'case_pallet_22' || profile === 'satex_case_2500'
      ? 'cases'
      : 'pallets';
  }

  function propertiesForVariant(catalog, variant) {
    var profile = profileForVariant(catalog, variant);
    var capacity = (catalog.profile || {}).container_capacity || 21;
    var cpp = Math.max(parseInt(variant.cases_per_pallet, 10) || 1, 1);
    if (profile === 'case_pallet_22') {
      return {
        _smj_case_order: 'true',
        _smj_cases_per_pallet: String(cpp),
        _smj_order_unit: 'cases',
        _smj_order_profile: 'case_pallet_22',
        _smj_container_capacity: String(capacity || 22),
        _smj_container_unit: 'pallets'
      };
    }
    if (profile === 'satex_case_2500') {
      return {
        _smj_case_order: 'true',
        _smj_order_unit: 'cases',
        _smj_order_profile: 'satex_case_2500',
        _smj_container_unit: 'cases',
        _smj_satex_category: String(variant.satex_category || '')
      };
    }
    return {};
  }

  function indexCatalog(payload) {
    state.variants = {};
    (payload.products || []).forEach(function (product) {
      (product.variants || []).forEach(function (variant) {
        state.variants[String(variant.id)] = {
          product: product,
          variant: variant
        };
      });
    });
  }

  function applyContext(payload) {
    var profile = payload.profile || {};
    var customer = payload.customer || {};
    var topLoadExempt = profile.top_load_exempt === true || window.smj_top_load_exempt === true;
    window.smjB2B = window.smjB2B || {};
    window.smjB2B.profile = profile.key || 'standard';
    window.smjB2B.topLoadExempt = topLoadExempt;
    window.smjB2B.topLoadForcedFamilies = Array.isArray(profile.top_load_forced_families)
      ? profile.top_load_forced_families
      : (window.smjB2B.topLoadForcedFamilies || []);
    window.smjB2B.capacity = profile.container_unit === 'cases'
      ? 21
      : profile.container_capacity || profile.pallet_limit || 21;
    window.smjB2B.palletLimit = profile.pallet_limit || window.smjB2B.capacity;
    window.smjB2B.unit = profile.order_unit || 'pallets';
    window.smjB2B.company = customer.company || window.smjB2B.company || '';
    window.smjB2B.additionalOrderModes = profile.additional_order_modes || [];
    window.smjB2B.mixed1l20ftEnabled = !!additionalOrderMode(
      { profile: profile },
      'mixed_1l_20ft'
    );
    if (Array.isArray(profile.container_scenarios)) {
      window.smjB2B.satexScenarios = profile.container_scenarios.map(function (scenario) {
        return {
          key: scenario.key,
          label: scenario.label,
          oneLitreCases: Number(scenario.one_litre_cases || 0),
          twoHundredMlCases: Number(scenario.two_hundred_ml_cases || 0)
        };
      });
    }
    window.smj_customer_order_profile = profile.key || 'standard';
    window.smj_order_profile = profile.key || 'standard';
    window.smj_standard_pallet_limit = profile.pallet_limit || 21;
    window.smj_pallets_per_container = window.smjB2B.capacity;
    window.smj_top_load_exempt = topLoadExempt;
    window.smj_top_load_forced_families = window.smjB2B.topLoadForcedFamilies;

    document.querySelectorAll('.smj-banner').forEach(function (banner) {
      banner.setAttribute('data-smj-api-authoritative', 'true');
    });
    document.querySelectorAll('.smj-banner-mode').forEach(function (mode) {
      if (profile.key === 'satex_case_2500') mode.textContent = 'Mixed ordering';
      else if (profile.key === 'case_pallet_22') mode.textContent = 'Cases mode';
      else mode.textContent = 'Pallet mode';
    });
    if (window.smjB2B.mixed1l20ftEnabled) {
      document.querySelectorAll('.smj-banner-links').forEach(function (nav) {
        if (nav.querySelector('[data-smj-20ft-link]')) return;
        var link = document.createElement('a');
        link.href = '/pages/build-container';
        link.textContent = 'Build 20ft container';
        link.setAttribute('data-smj-20ft-link', 'true');
        var ordersLink = Array.prototype.slice.call(nav.querySelectorAll('a')).filter(function (candidate) {
          return candidate.getAttribute('href') === '/pages/my-orders';
        })[0];
        nav.insertBefore(link, ordersLink || null);
      });
    }
  }

  function catalog() {
    if (state.catalogPromise) return state.catalogPromise;
    state.catalogPromise = request('catalog/').then(function (payload) {
      if (
        !payload ||
        payload.api_version !== 'v1' ||
        payload.authoritative !== true ||
        !Array.isArray(payload.products)
      ) {
        throw new Error('catalog_not_authoritative');
      }
      state.catalog = payload;
      indexCatalog(payload);
      applyContext(payload);
      return payload;
    });
    return state.catalogPromise;
  }

  function statusBadge(product, variants) {
    var tags = product.tags || [];
    var html = '';
    if (tags.indexOf('new_product') !== -1) {
      html += '<span class="smj-badge smj-badge--new">New</span> ';
    }
    if (tags.indexOf('coming_soon') !== -1) {
      html += '<span class="smj-badge smj-badge--soon">Coming soon</span> ';
    }
    if (tags.indexOf('just_added') !== -1) {
      html += '<span class="smj-badge smj-badge--stock">Just added</span> ';
    }
    var available = variants.some(function (variant) { return variant.available; });
    var lowStock = variants.some(function (variant) {
      return Number(variant.inventory_quantity) > 0 &&
        Number(variant.inventory_quantity) <= 100;
    });
    if (!available) return html + '<span class="smj-badge smj-badge--out">Out of stock</span>';
    return html + (
      lowStock
        ? '<span class="smj-badge smj-badge--low">Low stock</span>'
        : '<span class="smj-badge smj-badge--stock">In stock</span>'
    );
  }

  function collectionCard(catalogPayload, product) {
    var variants = regularVariants(product);
    if (!variants.length) return '';
    var availableVariants = variants.filter(function (variant) {
      return variant.available;
    });
    var variant = availableVariants[0] || variants[0];
    var image = product.images && product.images[0]
      ? product.images[0].src
      : '';
    var url = '/products/' + encodeURIComponent(product.handle);
    var unit = orderUnit(catalogPayload, variant);
    var cpp = parseInt(variant.cases_per_pallet, 10) || 0;
    var topLoad = variant.top_load || {};
    var satexScenario = profileForVariant(catalogPayload, variant) === 'satex_case_2500';
    var mixed20ftAvailable = !!(
      mixed20ftMode(catalogPayload) &&
      variant.mixed_1l_20ft_eligible &&
      variant.mixed_1l_20ft_variant &&
      variant.mixed_1l_20ft_variant.available
    );
    var unitPrice = priceCents(variant);
    var meta = cpp > 0 ? cpp + ' cases / pallet' : '';
    if (variants.length > 1) {
      meta += (meta ? ' · ' : '') + variants.length + ' options';
    }

    var action = '';
    if (!variant.available) {
      action = '<a class="smj-btn smj-btn--ghost" href="' + url + '">View product</a>';
    } else if (satexScenario) {
      action = '<a class="smj-btn smj-btn--cyan" href="/pages/build-container">Build container</a>';
    } else if (topLoad.required || variants.length > 1) {
      action = '<a class="smj-btn smj-btn--cyan" href="' + url + '">' +
        (topLoad.required ? 'Configure pallet' : 'Choose options') +
        '</a>' + (mixed20ftAvailable
          ? '<a class="smj-btn smj-btn--ghost" href="/pages/build-container">20ft case container</a>'
          : '');
    } else {
      var initial = unit === 'cases'
        ? Math.max(parseInt(variant.cases_per_pallet, 10) || 1, 1)
        : 1;
      var maximum = unit === 'pallets'
        ? parseInt((catalogPayload.profile || {}).pallet_limit, 10) ||
          (
            (catalogPayload.profile || {}).key === 'satex_case_2500'
              ? 21
              : ''
          )
        : '';
      action =
        '<form class="smj-api-product-form" data-variant="' + escapeAttribute(variant.id) + '">' +
          '<span class="smj-stepper">' +
            '<button type="button" data-step="minus" aria-label="Decrease quantity">−</button>' +
            '<input type="number" value="' + initial + '" min="' + initial + '" step="' + initial + '"' +
              (maximum ? ' max="' + maximum + '"' : '') +
              ' aria-label="' + escapeAttribute(unit + ' of ' + product.title) + '">' +
            '<button type="button" data-step="plus" aria-label="Increase quantity">+</button>' +
          '</span>' +
          '<button type="submit" class="smj-btn add-to-cart">Add ' + unit + '</button>' +
        '</form>';
    }

    return (
      '<div class="col-xs-6 col-sm-4 col-md-3">' +
        '<article class="smj-card" data-smj-title="' +
          escapeAttribute(product.title.toLowerCase()) + '">' +
          '<div class="smj-card-top">' +
            '<span class="smj-chip">' + escapeHtml(variant.sku || product.product_type || 'SMJ') + '</span>' +
            '<span>' + statusBadge(product, variants) + '</span>' +
          '</div>' +
          '<a class="smj-card-img" href="' + url + '">' +
            (image
              ? '<img src="' + escapeAttribute(image) + '" alt="' +
                  escapeAttribute((product.images[0] || {}).alt || product.title) + '">'
              : '') +
          '</a>' +
          '<a href="' + url + '"><h3 class="smj-card-title">' +
            escapeHtml(product.title) + '</h3></a>' +
          '<div class="smj-card-meta">' + escapeHtml(meta) + '</div>' +
          '<div class="smj-card-price">' +
            '<span class="smj-price-main">' + formatMoney(unitPrice) + '</span>' +
            '<span class="smj-price-unit">/ ' + unit.slice(0, -1) + '</span>' +
            (unit === 'pallets' && cpp > 0
              ? '<span class="smj-price-case">' +
                  formatMoney(Math.round(unitPrice / cpp)) + ' / case</span>'
              : '') +
          '</div>' +
          '<div class="smj-card-actions">' + action + '</div>' +
          '<div class="smj-card-unit-note">unit: ' + unit + '</div>' +
        '</article>' +
      '</div>'
    );
  }

  function hydrateCollection(payload) {
    var grid = document.getElementById('smj-collection-product-grid');
    if (!grid) return;
    var cards = (payload.products || []).map(function (product) {
      return collectionCard(payload, product);
    }).filter(Boolean);
    if (!cards.length) return;
    grid.innerHTML = cards.join('');
    grid.setAttribute('data-smj-api-authoritative', 'true');
    var title = document.querySelector('.smj-toolbar-title');
    if (title) title.textContent = cards.length + ' products in your catalog';
    document.querySelectorAll('[data-smj-sku-count]').forEach(function (count) {
      count.textContent = String(cards.length);
    });
  }

  function quickOrderRow(payload, product, variant) {
    var topLoad = variant.top_load || {};
    var unit = orderUnit(payload, variant);
    var cpp = Math.max(parseInt(variant.cases_per_pallet, 10) || 1, 1);
    var price = priceCents(variant);
    var displayTitle = variant.title && variant.title !== 'Default Title'
      ? variant.title
      : '';
    if (profileForVariant(payload, variant) === 'satex_case_2500') {
      return (
        '<tr data-smj-row data-configure="true">' +
          '<td class="smj-qo-product">' +
            escapeHtml(displayTitle || product.title) +
          '</td>' +
          '<td class="smj-qo-num" data-label="SKU">' + escapeHtml(variant.sku || '—') + '</td>' +
          '<td class="smj-qo-num" data-label="Category">' +
            (variant.satex_category === 'one_litre' ? '1L tetra' : '200ml tetra') +
          '</td>' +
          '<td class="smj-qo-num" data-label="Price">' + formatMoney(price) + ' / case</td>' +
          '<td colspan="3"><a class="smj-btn smj-btn--ghost smj-qo-configure" href="/pages/build-container">' +
            'Add in container planner</a></td>' +
        '</tr>'
      );
    }
    if (topLoad.required) {
      var mixed20ftAvailable = !!(
        mixed20ftMode(payload) &&
        variant.mixed_1l_20ft_eligible &&
        variant.mixed_1l_20ft_variant &&
        variant.mixed_1l_20ft_variant.available
      );
      return (
        '<tr data-smj-row data-configure="true">' +
          '<td class="smj-qo-product">' +
            escapeHtml(displayTitle || product.title) +
          '</td>' +
          '<td class="smj-qo-num" data-label="SKU">' + escapeHtml(variant.sku || '—') + '</td>' +
          '<td class="smj-qo-num" data-label="Cases / pallet">' + cpp + '</td>' +
          '<td class="smj-qo-num" data-label="Price">' + formatMoney(price) + '</td>' +
          '<td colspan="3"><a class="smj-btn smj-btn--ghost smj-qo-configure" href="/products/' +
            encodeURIComponent(product.handle) + '">Configure top-loaded pallet</a>' +
            (mixed20ftAvailable
              ? '<a class="smj-qo-alt-action" href="/pages/build-container">Or use 20ft case mode</a>'
              : '') +
          '</td>' +
        '</tr>'
      );
    }
    var step = unit === 'cases' ? cpp : 1;
    var properties = JSON.stringify(propertiesForVariant(payload, variant));
    return (
      '<tr data-smj-row data-variant="' + escapeAttribute(variant.id) +
        '" data-cpp="' + cpp +
        '" data-price="' + price +
        '" data-mode="' + unit +
        '" data-properties="' + escapeAttribute(properties) + '">' +
        '<td class="smj-qo-product">' +
          escapeHtml(displayTitle || product.title) +
        '</td>' +
        '<td class="smj-qo-num" data-label="SKU">' + escapeHtml(variant.sku || '—') + '</td>' +
        '<td class="smj-qo-num" data-label="Cases / pallet">' + cpp + '</td>' +
        '<td class="smj-qo-num" data-label="Price / ' + unit.slice(0, -1) + '">' +
          formatMoney(price) + '</td>' +
        '<td data-label="Qty (' + unit + ')">' +
          '<span class="smj-stepper smj-qo-stepper">' +
            '<button type="button" data-step="minus" aria-label="Decrease">−</button>' +
            '<input type="number" class="smj-qo-qty" min="0" step="' + step +
              '" placeholder="0" aria-label="' +
              escapeAttribute(unit + ' of ' + product.title + ' ' + displayTitle) + '">' +
            '<button type="button" data-step="plus" aria-label="Increase">+</button>' +
          '</span>' +
        '</td>' +
        '<td class="smj-qo-pallets" data-label="Conversion">—</td>' +
        '<td class="smj-qo-num smj-qo-line-total" data-label="Line total">—</td>' +
      '</tr>' +
      '<tr class="smj-qo-warning" style="display:none"><td colspan="7"></td></tr>'
    );
  }

  function quickOrderGroup(payload, product, groupIndex) {
    var rows = regularVariants(product).filter(function (variant) {
      return variant.available;
    }).map(function (variant) {
      return quickOrderRow(payload, product, variant);
    });
    if (!rows.length) return '';
    var expanded = groupIndex === 0;
    var groupId = 'smj-qo-api-group-' + groupIndex;
    return (
      '<tbody id="' + groupId + '" class="smj-qo-group' +
        (expanded ? '' : ' is-collapsed') + '">' +
        '<tr class="smj-qo-group-heading">' +
          '<th colspan="7">' +
            '<button type="button" class="smj-qo-group-toggle" ' +
              'data-smj-qo-group-toggle aria-expanded="' +
              (expanded ? 'true' : 'false') + '" aria-controls="' + groupId + '">' +
              '<span class="smj-qo-group-name">' + escapeHtml(product.title) + '</span>' +
              '<span class="smj-qo-group-meta">' +
                rows.length + ' ' + (rows.length === 1 ? 'option' : 'options') +
                '<span class="fa fa-angle-down" aria-hidden="true"></span>' +
              '</span>' +
            '</button>' +
          '</th>' +
        '</tr>' +
        rows.join('') +
      '</tbody>'
    );
  }

  function hydrateQuickOrder(payload) {
    var table = document.getElementById('smj-qo-table');
    if (!table) return;
    var groups = [];
    (payload.products || []).forEach(function (product) {
      var group = quickOrderGroup(payload, product, groups.length);
      if (group) groups.push(group);
    });
    if (!groups.length) return;
    Array.prototype.slice.call(table.tBodies).forEach(function (body) {
      body.remove();
    });
    table.insertAdjacentHTML('beforeend', groups.join(''));
    table.setAttribute('data-api-authoritative', 'true');
    table.setAttribute('data-mode', 'mixed');
    var headings = table.querySelectorAll('thead th');
    if (headings[3]) headings[3].textContent = 'Price / unit';
    if (headings[4]) headings[4].textContent = 'Order quantity';
    if (headings[5]) headings[5].textContent = 'Conversion';
    table.dispatchEvent(new CustomEvent('smj:quick-order:hydrated', {
      bubbles: true
    }));
  }

  function hydratePlanner(payload) {
    var list = document.getElementById('smj-planner-list');
    if (!list) return;
    var profile = (payload.profile || {}).key;
    var twentyFtMode = mixed20ftMode(payload);
    if (profile !== 'satex_case_2500' && !twentyFtMode) {
      list.innerHTML =
        '<div class="smj-empty smj-empty--compact">' +
          '<h4>Mixed-case ordering is not enabled for this account</h4>' +
          '<p>Your available pallet and case products remain in the catalog.</p>' +
          '<div class="smj-empty-actions"><a class="smj-btn smj-btn--cyan" href="/collections/all">Open catalog</a></div>' +
        '</div>';
      var nonSatexLock = document.getElementById('smj-planner-lock');
      if (nonSatexLock) nonSatexLock.classList.add('is-disabled');
      return;
    }

    if (twentyFtMode) {
      document.querySelectorAll('.smj-scenario-picker').forEach(function (picker) {
        picker.hidden = true;
      });
      var pageHead = document.querySelector('.smj-page-head');
      if (pageHead) {
        pageHead.innerHTML =
          '<h1>Build a 20ft mixed 1L container</h1>' +
          '<p>Allocate exactly ' + Number(twentyFtMode.container_capacity).toLocaleString() + ' cases across the eligible Fruta 1L and Trinidad reconstituted 1L flavours in your portfolio.</p>';
      }
      var toolbarTitle = document.querySelector('.smj-toolbar-title');
      if (toolbarTitle) toolbarTitle.textContent = 'Allocate eligible 1L flavours';
      var railTitle = document.querySelector('.smj-rail h3');
      if (railTitle) railTitle.textContent = '20ft container · 1L cases';
      list.setAttribute('data-planner-mode', 'mixed_1l_20ft');
    } else {
      list.setAttribute('data-planner-mode', 'satex_case_2500');
    }

    var rows = [];
    (payload.products || []).forEach(function (product) {
      regularVariants(product).forEach(function (variant) {
        var helper = variant.mixed_1l_20ft_variant || null;
        var eligible = twentyFtMode
          ? !!(variant.mixed_1l_20ft_eligible && helper && helper.available)
          : !!variant.satex_eligible;
        if (!variant.available || !eligible) return;
        var title = product.title + (
          variant.title && variant.title !== 'Default Title'
            ? ' · ' + variant.title
            : ''
        );
        var properties = twentyFtMode
          ? mixed20ftProperties(variant)
          : propertiesForVariant(payload, variant);
        var cartVariantId = twentyFtMode ? helper.id : variant.id;
        var casePrice = twentyFtMode ? priceCents(helper) : priceCents(variant);
        var category = twentyFtMode ? 'one_litre' : variant.satex_category;
        rows.push(
          '<div class="smj-planner-row" data-eligible data-variant="' +
            escapeAttribute(cartVariantId) + '" data-name="' +
            escapeAttribute(title) + '" data-category="' +
            escapeAttribute(category || '') + '" data-properties="' +
            escapeAttribute(JSON.stringify(properties)) + '">' +
            '<div class="smj-planner-info">' +
              '<b>' + escapeHtml(title) + '</b>' +
              '<small>' + escapeHtml(variant.sku || '') +
                (variant.sku ? ' · ' : '') + formatMoney(casePrice) +
                ' / case <span class="smj-badge smj-badge--eligible">Eligible</span></small>' +
            '</div>' +
            '<span class="smj-stepper">' +
              '<button type="button" data-step="minus" aria-label="Remove cases">−</button>' +
              '<input type="number" value="0" min="0" max="0" step="1" data-step-amount="5" disabled aria-label="' +
                escapeAttribute('Cases of ' + title) + '">' +
              '<button type="button" data-step="plus" aria-label="Add cases">+</button>' +
            '</span>' +
            '<span class="smj-planner-cases-label">cases</span>' +
          '</div>'
        );
      });
    });
    list.innerHTML = rows.length
      ? rows.join('')
      : '<div class="smj-empty smj-empty--compact"><h4>No price-ready eligible items are currently available</h4><p>Contact your representative if an expected 1L SKU is missing.</p></div>';
    list.setAttribute('data-api-authoritative', 'true');
    list.dispatchEvent(new CustomEvent('smj:planner:hydrated', {
      bubbles: true
    }));
  }

  function orderStatus(line) {
    if (line.status === 'available') {
      return '<span class="smj-review-flag is-ok">Available</span>';
    }
    if (line.status === 'configuration_required') {
      var href = line.product_handle
        ? '/products/' + encodeURIComponent(line.product_handle)
        : '/collections/all';
      return '<a class="smj-review-flag is-price" href="' + href + '">Configure pallet</a>';
    }
    if (line.status === 'unavailable') {
      return '<span class="smj-review-flag is-skip">Out of stock · skipped</span>';
    }
    return '<span class="smj-review-flag is-skip">No longer listed · skipped</span>';
  }

  function hydrateOrders() {
    var host = document.getElementById('smj-orders');
    if (!host) return Promise.resolve();
    return request('orders/').then(function (payload) {
      if (!payload || payload.authoritative !== true || !Array.isArray(payload.orders)) {
        throw new Error('orders_not_authoritative');
      }
      if (!payload.orders.length) {
        host.innerHTML =
          '<div class="smj-empty smj-empty--compact">' +
            '<h4>No submitted export orders yet</h4>' +
            '<p>Your submitted orders will appear here once they are recorded.</p>' +
            '<div class="smj-empty-actions"><a class="smj-btn smj-btn--cyan" href="/collections/all">Start an order</a></div>' +
          '</div>';
        return;
      }
      host.innerHTML = payload.orders.map(function (order, index) {
        var sheetId = 'smj-order-review-' + index;
        var items = [];
        var lines = (order.lines || []).map(function (line) {
          if (line.reorder_item) items.push(line.reorder_item);
          var quantity = Number(line.quantity || 0).toLocaleString() +
            ' ' + escapeHtml(line.order_unit || 'units');
          return (
            '<div class="smj-review-line">' +
              '<b>' + escapeHtml(line.product_title) +
                (line.variant_title ? ' · ' + escapeHtml(line.variant_title) : '') +
                ' · ' + quantity + '</b>' +
              orderStatus(line) +
            '</div>'
          );
        }).join('');
        var addAction = items.length
          ? '<button type="button" class="smj-btn smj-btn--cyan" data-reorder-add="' +
              escapeAttribute(JSON.stringify(items)) + '">Add available to cart</button>'
          : '<span>No lines can be reordered automatically.</span>';
        var unitSummary = Number(order.pallets || 0) > 0
          ? Number(order.pallets).toLocaleString() + ' pallets'
          : Number(order.cases || 0).toLocaleString() + ' cases';
        return (
          '<div class="smj-order-row">' +
            '<div class="smj-order-info">' +
              '<b>Order #' + escapeHtml(order.shopify_order_number || order.number) + '</b>' +
              '<span class="smj-order-status is-delivered">' +
                escapeHtml(order.status_label || 'Submitted') + '</span>' +
              '<small>' + escapeHtml(formatDate(order.created_at)) +
                (order.po_number ? ' · PO ' + escapeHtml(order.po_number) : '') +
                ' · ' + unitSummary + ' · ' +
                escapeHtml(order.containers || '0') + ' containers</small>' +
            '</div>' +
            '<div class="smj-order-actions">' +
              '<button type="button" class="smj-btn smj-btn--cyan" data-reorder-toggle="' +
                sheetId + '">Reorder</button>' +
            '</div>' +
          '</div>' +
          '<div class="smj-review-sheet" id="' + sheetId + '">' +
            '<h4>Review order #' +
              escapeHtml(order.shopify_order_number || order.number) + '</h4>' +
            lines +
            '<div class="smj-review-foot">' +
              '<span>' + items.length + ' of ' + Number(order.line_count || 0) +
                ' lines can be added now.</span>' +
              addAction +
            '</div>' +
          '</div>'
        );
      }).join('');
    }).catch(function () {
      host.setAttribute('data-smj-api-unavailable', 'true');
    });
  }

  function savedCartCard(cart, writesEnabled) {
    var available = (cart.lines || []).map(function (line) {
      return line.reorder_item;
    }).filter(Boolean);
    var details = (cart.lines || []).slice(0, 4).map(function (line) {
      return line.product_title;
    }).join(', ');
    return (
      '<article class="smj-template-card" data-saved-cart="' +
        escapeAttribute(cart.id) + '">' +
        '<b data-saved-name>' + escapeHtml(cart.name) + '</b>' +
        (writesEnabled
          ? '<form class="smj-saved-rename" data-saved-rename-form="' +
              escapeAttribute(cart.id) + '" hidden>' +
              '<input name="name" maxlength="120" required value="' +
                escapeAttribute(cart.name) + '" aria-label="Saved cart name">' +
              '<button type="submit" class="smj-icon-btn" title="Save name" aria-label="Save name">' +
                '<span class="fa fa-check" aria-hidden="true"></span></button>' +
              '<button type="button" class="smj-icon-btn" data-saved-rename-cancel title="Cancel rename" aria-label="Cancel rename">' +
                '<span class="fa fa-times" aria-hidden="true"></span></button>' +
            '</form>'
          : '') +
        '<span class="smj-chip">' + Number(cart.line_count || 0) + ' SKUs · ' +
          Number(cart.total_quantity || 0).toLocaleString() + ' units</span>' +
        '<small>' + escapeHtml(details) + '</small>' +
        '<small>Updated ' + escapeHtml(formatDate(cart.updated_at)) + '</small>' +
        '<div class="smj-template-actions">' +
          (available.length
            ? '<button type="button" class="smj-btn smj-btn--cyan" data-saved-load="' +
                escapeAttribute(cart.id) + '" data-items="' +
                escapeAttribute(JSON.stringify(available)) + '">Load into cart</button>'
            : '<span class="smj-review-flag is-skip">No available lines</span>') +
          (writesEnabled
            ? '<button type="button" class="smj-icon-btn" data-saved-rename="' +
                escapeAttribute(cart.id) + '" title="Rename saved cart" aria-label="Rename saved cart">' +
                '<span class="fa fa-pencil" aria-hidden="true"></span></button>' +
              '<button type="button" class="smj-icon-btn smj-icon-btn--danger" data-saved-delete="' +
                escapeAttribute(cart.id) + '" title="Delete saved cart" aria-label="Delete saved cart">' +
                '<span class="fa fa-trash" aria-hidden="true"></span></button>'
            : '') +
        '</div>' +
      '</article>'
    );
  }

  function renderSavedCarts(payload) {
    var host = document.getElementById('smj-templates');
    if (!host) return;
    var carts = payload.saved_carts || [];
    var writes = state.savedCartWrites;
    var html = carts.map(function (cart) {
      return savedCartCard(cart, writes);
    }).join('');
    if (!html) {
      html =
        '<div class="smj-empty smj-empty--compact">' +
          '<h4>No saved carts yet</h4>' +
          '<p>Save a prepared cart to make the next repeat order faster.</p>' +
        '</div>';
    }
    if (writes) {
      html +=
        '<form class="smj-template-card smj-template-card--new" id="smj-api-save-cart">' +
          '<label for="smj-saved-cart-name">Template name</label>' +
          '<input id="smj-saved-cart-name" name="name" maxlength="120" required placeholder="Monthly container">' +
          '<button class="smj-btn smj-btn--cyan" type="submit">Save current cart</button>' +
        '</form>';
    }
    host.innerHTML = html;
    host.setAttribute('data-api-authoritative', 'true');
  }

  function loadSavedCarts() {
    var host = document.getElementById('smj-templates');
    if (!host) return Promise.resolve();
    return Promise.all([catalog(), request('saved-carts/')]).then(function (results) {
      var payload = results[1];
      if (!payload || payload.authoritative !== true) {
        throw new Error('saved_carts_not_authoritative');
      }
      state.savedCartWrites = !!(
        state.catalog &&
        state.catalog.capabilities &&
        state.catalog.capabilities.saved_cart_writes
      );
      renderSavedCarts(payload);
    }).catch(function () {
      host.setAttribute('data-smj-api-unavailable', 'true');
    });
  }

  function addItems(items, button) {
    if (!items.length) return Promise.reject(new Error('empty_items'));
    if (button) button.classList.add('is-disabled');
    return fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (!response.ok) throw new Error('cart_add_failed');
      return response.json();
    }).then(function (result) {
      if (window.CartJS) window.CartJS.getCart();
      return result;
    }).catch(function (error) {
      if (button) button.classList.remove('is-disabled');
      throw error;
    });
  }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'smj-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  }

  function normalizedProductText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function compactProductText(value) {
    return normalizedProductText(value).replace(/\s+/g, '');
  }


  /* Profiles exempt from top-load may still force it for named families
     (window.smjB2B.topLoadForcedFamilies). TGG: Fruta Premium 475ml only. */
  function forcedTopLoadFamily(productTitle, variantTitle) {
    var families = (window.smjB2B || {}).topLoadForcedFamilies ||
      window.smj_top_load_forced_families || [];
    if (!families.length) return false;
    var product = normalizedProductText(productTitle);
    var variant = compactProductText(variantTitle);
    var is475 = ['475ml', '500ml', '520ml', '17oz'].some(function (size) {
      return variant.indexOf(size) !== -1;
    });
    return families.some(function (key) {
      return key === 'fruta_premium_475' && is475 &&
        product.indexOf('fruta premium') !== -1;
    });
  }

  function productNeedsTopLoad(productTitle, variantTitle) {
    if (
      ((window.smjB2B || {}).topLoadExempt || window.smj_top_load_exempt) &&
      !forcedTopLoadFamily(productTitle, variantTitle)
    ) {
      return false;
    }
    var product = normalizedProductText(productTitle);
    var variant = compactProductText(variantTitle);
    var oneLitre = variant.indexOf('1l') !== -1 ||
      variant.indexOf('1000ml') !== -1;
    var onePointFiveLitre = variant.indexOf('15l') !== -1 ||
      variant.indexOf('1500ml') !== -1;
    var changed475Size = [
      '475ml',
      '500ml',
      '520ml',
      '17oz'
    ].some(function (size) {
      return variant.indexOf(size) !== -1;
    });

    if (
      oneLitre &&
      (
        product.indexOf('fruta premium') !== -1 ||
        product.indexOf('fruta tetra') !== -1 ||
        product.indexOf('trinidad juice') !== -1
      )
    ) {
      return true;
    }
    if (
      onePointFiveLitre &&
      product.indexOf('oasis purified water') !== -1
    ) {
      return true;
    }
    return changed475Size && (
      product.indexOf('caribbean cool') !== -1 ||
      product.indexOf('fruta premium') !== -1 ||
      product.indexOf('fruta max') !== -1 ||
      product.indexOf('altitude') !== -1
    );
  }

  function hideManagedHelperOptions() {
    document.querySelectorAll(
      '#productSelect option, .single-option-selector option, variant-radios label'
    ).forEach(function (element) {
      if (
        /\(\s*top\s*loaded?\s*-/i.test(element.textContent || '') ||
        /\(\s*20ft\s+case\s+order\s*\)/i.test(element.textContent || '')
      ) {
        element.hidden = true;
        if (element.tagName === 'OPTION') element.disabled = true;
      }
    });
  }

  function selectedFallbackVariantTitle() {
    var select = document.getElementById('productSelect');
    var hidden = document.querySelector(
      '#product-details form[data-cart-submit] input[name="id"]'
    );
    var id = select ? select.value : hidden ? hidden.value : '';
    return String((window.variants_title || {})[id] || '');
  }

  function positionTopLoadHost(host, form) {
    if (!host || !form) return;
    var purchaseControls = form.querySelector('#price-field, .product-stock, .product-actions');
    if (purchaseControls) {
      form.insertBefore(host, purchaseControls);
    } else {
      form.appendChild(host);
    }
  }

  function setProductPurchaseVisibility(form, topLoadRequired) {
    if (!form) return;
    form.style.removeProperty('display');
    form.querySelectorAll('#price-field, .product-stock, .product-actions').forEach(function (element) {
      element.hidden = topLoadRequired;
      // Theme CSS gives .product-actions `display: flex`, which overrides the
      // hidden attribute; force the display so the plain Add to Cart cannot
      // sit next to the required top-load builder.
      element.style.display = topLoadRequired ? 'none' : '';
    });
  }

  function fallbackProductSafety() {
    if (!apiConfig.productId) return;
    var form = document.querySelector('#product-details form[data-cart-submit]');
    var legacy = document.querySelectorAll('.mixed-pallet-container');
    var required = productNeedsTopLoad(
      window.smj_product_title,
      selectedFallbackVariantTitle()
    );
    var host = document.getElementById('smj-api-top-load');

    if (!required) {
      setProductPurchaseVisibility(form, false);
      legacy.forEach(function (element) {
        element.style.removeProperty('display');
      });
      if (host) host.hidden = true;
      return;
    }

    if (!host) {
      host = document.createElement('section');
      host.id = 'smj-api-top-load';
      host.className = 'smj-top-load-builder';
      host.setAttribute('aria-live', 'polite');
      positionTopLoadHost(host, form);
    }
    positionTopLoadHost(host, form);
    setProductPurchaseVisibility(form, true);
    legacy.forEach(function (element) {
      element.style.display = 'none';
    });
    if (!host) return;
    host.removeAttribute('aria-busy');
    host.innerHTML =
      '<div class="smj-top-load-head">' +
        '<div><span class="smj-eyebrow">Required configuration</span>' +
        '<h2>Top-loaded pallet</h2></div>' +
        '<span class="smj-review-flag is-price">Temporarily unavailable</span>' +
      '</div>' +
      '<p>We could not verify the approved base-layer options. Refresh the page or contact your representative before ordering this SKU.</p>' +
      '<a class="smj-btn smj-btn--ghost" href="mailto:exportsupport@smj.com">Contact your representative</a>';
    host.hidden = false;
  }

  function showTopLoadLoading() {
    if (!apiConfig.productId) return;
    if (
      !productNeedsTopLoad(
        window.smj_product_title,
        selectedFallbackVariantTitle()
      )
    ) {
      return;
    }
    var form = document.querySelector('#product-details form[data-cart-submit]');
    var legacy = document.querySelectorAll('.mixed-pallet-container');
    var host = document.getElementById('smj-api-top-load');
    if (!host) {
      host = document.createElement('section');
      host.id = 'smj-api-top-load';
      host.className = 'smj-top-load-builder';
      host.setAttribute('aria-live', 'polite');
      positionTopLoadHost(host, form);
    }
    if (!host) return;
    positionTopLoadHost(host, form);
    setProductPurchaseVisibility(form, true);
    legacy.forEach(function (element) {
      element.style.display = 'none';
    });
    host.setAttribute('aria-busy', 'true');
    host.innerHTML =
      '<div class="smj-top-load-head">' +
        '<div><span class="smj-eyebrow">Required configuration</span>' +
        '<h2>Preparing pallet options</h2></div>' +
        '<span class="smj-review-flag">Loading</span>' +
      '</div>' +
      '<div class="smj-top-load-skeleton" aria-hidden="true">' +
        '<span></span><span></span><span></span><span></span>' +
      '</div>';
    host.hidden = false;
  }

  function topLoadMainOptions(product, selectedId) {
    return regularVariants(product).filter(function (candidate) {
      var topLoad = candidate.top_load || {};
      return topLoad.required && topLoad.ready;
    }).map(function (candidate) {
      var cases = Number(
        ((candidate.top_load || {}).main || {}).cases || 0
      ).toLocaleString();
      return '<option value="' + escapeAttribute(candidate.id) + '"' +
        (String(candidate.id) === String(selectedId) ? ' selected' : '') + '>' +
        escapeHtml(candidate.title + ' · ' + cases + ' cases') +
      '</option>';
    }).join('');
  }

  function topLoadBaseOptions(options) {
    var groups = [];
    var byLabel = {};
    options.forEach(function (option, index) {
      var label = option.family_label || option.product_title || 'Approved bases';
      if (!byLabel[label]) {
        byLabel[label] = [];
        groups.push(label);
      }
      byLabel[label].push(
        '<option value="' + index + '">' +
          escapeHtml(
            (option.variant_title || option.product_title) +
            ' · ' + Number(option.cases).toLocaleString() + ' cases'
          ) +
        '</option>'
      );
    });
    return groups.map(function (label) {
      return '<optgroup label="' + escapeAttribute(label) + '">' +
        byLabel[label].join('') +
      '</optgroup>';
    }).join('');
  }

  function updateProductHeroImage(variant) {
    var image = (variant || {}).image || {};
    var element = document.querySelector('.product-gallery-container img');
    if (!element || !image.src) return;
    element.src = image.src;
    element.setAttribute('data-src', image.src);
    element.removeAttribute('srcset');
    if (image.alt) element.alt = image.alt;
  }

  function mixed20ftProductChoice(variant) {
    var helper = (variant || {}).mixed_1l_20ft_variant || null;
    if (
      !state.catalog ||
      !mixed20ftMode(state.catalog) ||
      !(variant || {}).mixed_1l_20ft_eligible ||
      !helper ||
      !helper.available
    ) {
      return '';
    }
    return (
      '<div class="smj-top-load-alternative">' +
        '<span class="smj-top-load-or">Or</span>' +
        '<div><b>Order a 20ft mixed 1L container</b>' +
          '<small>Enter case quantities across eligible flavours. The order must total exactly ' + Number(mixed20ftMode(state.catalog).container_capacity).toLocaleString() + ' cases.</small></div>' +
        '<a class="smj-btn smj-btn--ghost" href="/pages/build-container">Build 20ft container</a>' +
      '</div>'
    );
  }

  function topLoadWidget(product, variant) {
    var config = variant.top_load || {};
    var host = document.getElementById('smj-api-top-load');
    if (!host) {
      host = document.createElement('section');
      host.id = 'smj-api-top-load';
      host.className = 'smj-top-load-builder';
      host.setAttribute('aria-live', 'polite');
      var details = document.getElementById('product-details');
      var form = details && details.querySelector('form[data-cart-submit]');
      positionTopLoadHost(host, form);
    }
    if (!host) return;
    host.removeAttribute('aria-busy');
    updateProductHeroImage(variant);

    if (!config.ready || !config.main || !(config.base_options || []).length) {
      host.innerHTML =
        '<div class="smj-top-load-head">' +
          '<div><span class="smj-eyebrow">Required configuration</span>' +
          '<h2>Top-loaded pallet</h2></div>' +
          '<span class="smj-review-flag is-price">Pending setup</span>' +
        '</div>' +
        '<p>This SKU requires a paired base layer. Ordering is paused until its price-correct configuration is available.</p>' +
        '<a class="smj-btn smj-btn--ghost" href="mailto:exportsupport@smj.com">Contact your representative</a>' +
        mixed20ftProductChoice(variant);
      host.hidden = false;
      return;
    }

    var mainOptions = topLoadMainOptions(product, variant.id);
    var options = topLoadBaseOptions(config.base_options);
    var customerProfile = state.catalog.profile || {};
    var max = customerProfile.key === 'case_pallet_22'
      ? 0
      : parseInt(customerProfile.pallet_limit, 10) || 21;
    var maxAttribute = max ? ' max="' + max + '"' : '';
    host.innerHTML =
      '<div class="smj-top-load-head">' +
        '<div><span class="smj-eyebrow">Required configuration</span>' +
        '<h2>Build a top-loaded pallet</h2></div>' +
        '<span class="smj-review-flag is-ok">Approved pairing</span>' +
      '</div>' +
      '<div class="smj-top-load-layers">' +
        '<div class="smj-top-load-layer">' +
          '<label for="smj-top-load-main">Main layer flavour</label>' +
          '<b>' + escapeHtml(product.title) + '</b>' +
          '<span class="smj-select-wrap">' +
            '<select id="smj-top-load-main">' + mainOptions + '</select>' +
            '<span class="fa fa-chevron-down" aria-hidden="true"></span>' +
          '</span>' +
          '<small>' + Number(config.main.cases).toLocaleString() + ' cases per pallet</small>' +
        '</div>' +
        '<div class="smj-top-load-layer">' +
          '<label for="smj-top-load-base">Base layer product and flavour</label>' +
          '<div class="smj-base-preview" id="smj-top-load-base-preview" hidden>' +
            '<img id="smj-top-load-base-image" src="" alt="" width="64" height="64">' +
            '<div>' +
              '<b id="smj-top-load-base-title"></b>' +
              '<small id="smj-top-load-base-detail"></small>' +
            '</div>' +
          '</div>' +
          '<span class="smj-select-wrap">' +
            '<select id="smj-top-load-base">' + options + '</select>' +
            '<span class="fa fa-chevron-down" aria-hidden="true"></span>' +
          '</span>' +
        '</div>' +
      '</div>' +
      '<div class="smj-top-load-actions">' +
        '<label for="smj-top-load-quantity">Pallets</label>' +
        '<span class="smj-stepper">' +
          '<button type="button" data-step="minus" aria-label="Decrease pallets">−</button>' +
          '<input id="smj-top-load-quantity" type="number" value="1" min="1"' +
            maxAttribute + ' step="1">' +
          '<button type="button" data-step="plus" aria-label="Increase pallets">+</button>' +
        '</span>' +
        '<div class="smj-top-load-total" id="smj-top-load-total"></div>' +
        '<button type="button" class="smj-btn smj-btn--cyan" id="smj-top-load-add">Add paired pallets</button>' +
      '</div>' +
      '<p class="smj-summary-note">Each paired main and base layer counts as one physical pallet in the cart summary.</p>';
    host.innerHTML += mixed20ftProductChoice(variant);
    host.hidden = false;
    host.dataset.variantId = String(variant.id);
    updateTopLoadTotal();
  }

  function selectedProductVariant() {
    var select = document.getElementById('productSelect');
    var hidden = document.querySelector(
      '#product-details form[data-cart-submit] input[name="id"]'
    );
    var id = select ? select.value : hidden ? hidden.value : '';
    var row = state.variants[String(id)];
    return row || null;
  }

  function updateTopLoadTotal() {
    var host = document.getElementById('smj-api-top-load');
    var total = document.getElementById('smj-top-load-total');
    var basePreview = document.getElementById('smj-top-load-base-preview');
    var baseImage = document.getElementById('smj-top-load-base-image');
    var baseTitle = document.getElementById('smj-top-load-base-title');
    var baseDetail = document.getElementById('smj-top-load-base-detail');
    if (!host || !total) return;
    var row = state.variants[String(host.dataset.variantId || '')];
    if (!row) return;
    var config = row.variant.top_load || {};
    var baseIndex = parseInt(
      (document.getElementById('smj-top-load-base') || {}).value,
      10
    ) || 0;
    var quantity = Math.max(
      parseInt((document.getElementById('smj-top-load-quantity') || {}).value, 10) || 1,
      1
    );
    var base = (config.base_options || [])[baseIndex];
    if (!base || !config.main) return;
    var image = base.image || {};
    if (basePreview && baseImage) {
      if (image.src) {
        baseImage.src = image.src;
        baseImage.alt = image.alt || (
          (base.product_title || '') + ' ' + (base.variant_title || '')
        ).trim();
        basePreview.hidden = false;
      } else {
        baseImage.removeAttribute('src');
        baseImage.alt = '';
        basePreview.hidden = true;
      }
    }
    if (baseTitle) {
      baseTitle.textContent = base.product_title || base.family_label || '';
    }
    if (baseDetail) {
      baseDetail.textContent =
        (base.variant_title || base.family_label || '') +
        ' · ' + Number(base.cases).toLocaleString() + ' cases';
    }
    var perPallet = Math.round(
      (parseFloat(config.main.price || 0) + parseFloat(base.price || 0)) * 100
    );
    total.innerHTML =
      '<span>' + Number(config.main.cases).toLocaleString() + ' + ' +
        Number(base.cases).toLocaleString() + ' cases per pallet</span>' +
      '<b>' + formatMoney(perPallet * quantity) + '</b>';
  }

  function syncProductTopLoad() {
    if (!state.catalog || !apiConfig.productId) return;
    var product = (state.catalog.products || []).filter(function (candidate) {
      return String(candidate.id) === String(apiConfig.productId);
    })[0];
    if (!product) {
      fallbackProductSafety();
      return;
    }
    var selected = selectedProductVariant();
    var form = document.querySelector('#product-details form[data-cart-submit]');
    var legacy = document.querySelectorAll('.mixed-pallet-container');
    var host = document.getElementById('smj-api-top-load');
    var required = !!(
      selected &&
      selected.product.id === product.id &&
      (selected.variant.top_load || {}).required
    );
    legacy.forEach(function (element) {
      element.style.display = required ? 'none' : '';
    });
    setProductPurchaseVisibility(form, required);
    if (host && required) positionTopLoadHost(host, form);
    if (host && !required) host.hidden = true;
    if (required) topLoadWidget(product, selected.variant);
  }

  function hydrateProduct() {
    if (!apiConfig.productId) return;
    hideManagedHelperOptions();
    document.addEventListener('change', function (event) {
      if (
        event.target.id === 'productSelect' ||
        event.target.closest('variant-radios')
      ) {
        window.setTimeout(syncProductTopLoad, 0);
      }
    });
    syncProductTopLoad();
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('.smj-api-product-form');
    if (!form || !state.catalog) return;
    event.preventDefault();
    var row = state.variants[String(form.getAttribute('data-variant') || '')];
    var input = form.querySelector('input[type="number"]');
    var quantity = parseInt(input && input.value, 10) || 0;
    if (!row || quantity <= 0) return;
    var item = {
      id: Number(row.variant.id),
      quantity: quantity,
      properties: propertiesForVariant(state.catalog, row.variant)
    };
    addItems([item], form.querySelector('[type="submit"]')).then(function () {
      window.location.href = '/cart';
    }).catch(function () {});
  });

  document.addEventListener('change', function (event) {
    if (event.target.id === 'smj-top-load-main') {
      var selectedId = String(event.target.value || '');
      var selectedRow = state.variants[selectedId];
      if (selectedRow) updateProductHeroImage(selectedRow.variant);
      var productSelect = document.getElementById('productSelect');
      var hiddenVariant = document.querySelector(
        '#product-details form[data-cart-submit] input[name="id"]'
      );
      if (productSelect) {
        productSelect.value = selectedId;
        productSelect.dispatchEvent(new Event('change', { bubbles: true }));
        window.setTimeout(syncProductTopLoad, 0);
      } else if (hiddenVariant) {
        hiddenVariant.value = selectedId;
        syncProductTopLoad();
      }
      return;
    }
    if (
      event.target.id === 'smj-top-load-base' ||
      event.target.id === 'smj-top-load-quantity'
    ) {
      updateTopLoadTotal();
    }
  });

  document.addEventListener('input', function (event) {
    if (event.target.id === 'smj-top-load-quantity') updateTopLoadTotal();
  });

  document.addEventListener('click', function (event) {
    var topLoadAdd = event.target.closest('#smj-top-load-add');
    if (topLoadAdd) {
      event.preventDefault();
      var host = document.getElementById('smj-api-top-load');
      var row = state.variants[String((host || {}).dataset.variantId || '')];
      var baseIndex = parseInt(
        (document.getElementById('smj-top-load-base') || {}).value,
        10
      ) || 0;
      var quantity = Math.max(
        parseInt((document.getElementById('smj-top-load-quantity') || {}).value, 10) || 1,
        1
      );
      var config = row && row.variant.top_load;
      var base = config && (config.base_options || [])[baseIndex];
      if (!config || !config.main || !base) return;
      var uniqueId = uuid();
      var mainProperties = Object.assign(
        { _unique_id: uniqueId },
        config.main.properties || {}
      );
      var baseProperties = Object.assign(
        { _unique_id: uniqueId },
        base.properties || {}
      );
      addItems([
        {
          id: Number(config.main.variant_id),
          quantity: quantity,
          properties: mainProperties
        },
        {
          id: Number(base.variant_id),
          quantity: quantity,
          properties: baseProperties
        }
      ], topLoadAdd).then(function () {
        window.location.href = '/cart';
      }).catch(function () {});
      return;
    }

    var savedLoad = event.target.closest('[data-saved-load]');
    if (savedLoad) {
      event.preventDefault();
      var items = [];
      try {
        items = JSON.parse(savedLoad.getAttribute('data-items') || '[]');
      } catch (error) {
        items = [];
      }
      addItems(items, savedLoad).then(function () {
        if (!state.savedCartWrites) {
          window.location.href = '/cart';
          return;
        }
        request('saved-carts/' + savedLoad.getAttribute('data-saved-load') + '/use/', {
          method: 'POST'
        }).finally(function () {
          window.location.href = '/cart';
        });
      }).catch(function () {});
      return;
    }

    var savedDelete = event.target.closest('[data-saved-delete]');
    if (savedDelete) {
      event.preventDefault();
      var deleteCard = savedDelete.closest('[data-saved-cart]');
      var deleteName = deleteCard && deleteCard.querySelector('[data-saved-name]');
      if (!window.confirm(
        'Delete saved cart "' +
        String(deleteName ? deleteName.textContent : 'this cart').trim() +
        '"?'
      )) {
        return;
      }
      request('saved-carts/' + savedDelete.getAttribute('data-saved-delete') + '/', {
        method: 'DELETE'
      }).then(loadSavedCarts).catch(function () {
        savedDelete.classList.remove('is-disabled');
      });
      return;
    }

    var savedRename = event.target.closest('[data-saved-rename]');
    if (savedRename) {
      event.preventDefault();
      var card = savedRename.closest('[data-saved-cart]');
      var renameForm = card && card.querySelector('[data-saved-rename-form]');
      var savedName = card && card.querySelector('[data-saved-name]');
      if (renameForm) {
        renameForm.hidden = false;
        if (savedName) savedName.hidden = true;
        var renameInput = renameForm.querySelector('input[name="name"]');
        if (renameInput) {
          renameInput.focus();
          renameInput.select();
        }
      }
      return;
    }

    var renameCancel = event.target.closest('[data-saved-rename-cancel]');
    if (renameCancel) {
      event.preventDefault();
      var cancelCard = renameCancel.closest('[data-saved-cart]');
      var cancelForm = cancelCard && cancelCard.querySelector('[data-saved-rename-form]');
      var cancelName = cancelCard && cancelCard.querySelector('[data-saved-name]');
      if (cancelForm) cancelForm.hidden = true;
      if (cancelName) cancelName.hidden = false;
    }
  });

  document.addEventListener('submit', function (event) {
    var renameForm = event.target.closest('[data-saved-rename-form]');
    if (renameForm) {
      event.preventDefault();
      var renameButton = renameForm.querySelector('[type="submit"]');
      if (renameButton) renameButton.classList.add('is-disabled');
      request(
        'saved-carts/' + renameForm.getAttribute('data-saved-rename-form') + '/',
        {
          method: 'PUT',
          body: { name: renameForm.elements.name.value }
        }
      ).then(loadSavedCarts).catch(function () {
        if (renameButton) renameButton.classList.remove('is-disabled');
      });
      return;
    }

    var form = event.target.closest('#smj-api-save-cart');
    if (!form) return;
    event.preventDefault();
    var button = form.querySelector('[type="submit"]');
    button.classList.add('is-disabled');
    Promise.all([
      fetch(routeRoot() + 'cart.js', { credentials: 'same-origin' }).then(function (response) {
        return response.json();
      }),
      catalog()
    ]).then(function (results) {
      var cart = results[0];
      if (!cart.items || !cart.items.length) throw new Error('empty_cart');
      return request('saved-carts/', {
        method: 'POST',
        body: {
          name: form.elements.name.value,
          items: cart.items.map(function (item) {
            return {
              id: item.variant_id,
              quantity: item.quantity,
              properties: item.properties || {}
            };
          })
        }
      });
    }).then(loadSavedCarts).catch(function () {
      button.classList.remove('is-disabled');
    });
  });

  window.smjB2BApi = {
    catalog: catalog,
    request: request,
    addItems: addItems
  };

  function markApiSurfacesBusy() {
    [
      'smj-collection-product-grid',
      'smj-qo-table',
      'smj-planner-list',
      'smj-orders',
      'smj-templates'
    ].forEach(function (id) {
      var element = document.getElementById(id);
      if (!element) return;
      element.setAttribute('aria-busy', 'true');
      element.setAttribute('aria-label', 'Loading account data');
    });
  }

  function completeApiLoading() {
    document.body.classList.remove('smj-api-pending');
    [
      'smj-collection-product-grid',
      'smj-qo-table',
      'smj-planner-list',
      'smj-orders',
      'smj-templates'
    ].forEach(function (id) {
      var element = document.getElementById(id);
      if (!element) return;
      element.removeAttribute('aria-busy');
      element.removeAttribute('aria-label');
    });
  }

  function boot() {
    if (!apiEnabled()) {
      completeApiLoading();
      hideManagedHelperOptions();
      fallbackProductSafety();
      document.addEventListener('change', function (event) {
        if (
          event.target.id === 'productSelect' ||
          event.target.closest('variant-radios')
        ) {
          window.setTimeout(fallbackProductSafety, 0);
        }
      });
      return;
    }
    hideManagedHelperOptions();
    showTopLoadLoading();
    markApiSurfacesBusy();
    var tasks = [];
    var catalogTask = catalog().then(function (payload) {
      hydrateCollection(payload);
      hydrateQuickOrder(payload);
      hydratePlanner(payload);
      hydrateProduct();
    }).catch(function () {
      fallbackProductSafety();
      document.addEventListener('change', function (event) {
        if (
          event.target.id === 'productSelect' ||
          event.target.closest('variant-radios')
        ) {
          window.setTimeout(fallbackProductSafety, 0);
        }
      });
      if (typeof window.smjHydrateLegacyCollection === 'function') {
        window.smjHydrateLegacyCollection();
      }
    });
    tasks.push(catalogTask);
    if (document.getElementById('smj-orders')) tasks.push(hydrateOrders());
    if (document.getElementById('smj-templates')) tasks.push(loadSavedCarts());
    Promise.allSettled(tasks).then(completeApiLoading);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
